import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

const AboutHero = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"]
  });

  // Staggered content reveals
  const eyebrowOpacity = useTransform(scrollYProgress, [0, 0.15, 0.4], [1, 1, 0]);
  const eyebrowY = useTransform(scrollYProgress, [0, 0.4], [0, -50]);

  const headlineOpacity = useTransform(scrollYProgress, [0, 0.2, 0.5], [1, 1, 0]);
  const headlineY = useTransform(scrollYProgress, [0, 0.5], [0, -60]);

  const descriptionOpacity = useTransform(scrollYProgress, [0, 0.25, 0.6], [1, 1, 0]);
  const descriptionY = useTransform(scrollYProgress, [0, 0.6], [0, -70]);

  // Background glow intensity
  const glowIntensity = useTransform(scrollYProgress, [0, 0.3, 0.7], [0.05, 0.15, 0.05]);

  return (
    <section ref={containerRef} className="relative bg-dark-bg min-h-[85vh] lg:min-h-[100vh] overflow-hidden">
      {/* Continuous Background Grid */}
      <div className="fixed inset-0 pointer-events-none opacity-20">
        <div className="absolute inset-0" style={{
          backgroundImage: `
            linear-gradient(rgba(0, 102, 255, 0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0, 102, 255, 0.1) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px'
        }} />
      </div>

      {/* Dynamic Background Glows - DESKTOP ONLY */}
      <motion.div 
        className="hidden lg:block absolute top-20 left-10 w-96 h-96 bg-accent-blue rounded-full blur-3xl pointer-events-none"
        style={{ opacity: glowIntensity }}
      />
      <motion.div 
        className="hidden lg:block absolute bottom-20 right-10 w-96 h-96 bg-accent-blue rounded-full blur-3xl pointer-events-none"
        style={{ opacity: glowIntensity }}
      />

      {/* Static Background Glows - MOBILE */}
      <div className="lg:hidden absolute top-20 left-10 w-96 h-96 bg-accent-blue/5 rounded-full blur-3xl pointer-events-none" />
      <div className="lg:hidden absolute bottom-20 right-10 w-96 h-96 bg-accent-blue/5 rounded-full blur-3xl pointer-events-none" />

      {/* DESKTOP - Scroll-animated content */}
      <div className="hidden lg:block sticky top-0 h-screen flex items-center justify-center">
        <div className="relative z-10 max-w-[1200px] mx-auto px-6 md:px-8 text-center">
          {/* Eyebrow */}
          <motion.p
            style={{ opacity: eyebrowOpacity, y: eyebrowY }}
            className="text-sm md:text-base text-accent-blue font-semibold mb-4 md:mb-6 uppercase tracking-wider"
          >
            About ForceM
          </motion.p>

          {/* Headline */}
          <motion.h1
            style={{ opacity: headlineOpacity, y: headlineY }}
            className="text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold text-white mb-6 md:mb-8 leading-tight"
          >
            We identify and remove
            <br className="hidden md:block" />
            <span className="text-accent-blue"> growth bottlenecks</span>
          </motion.h1>

          {/* Description */}
          <motion.p
            style={{ opacity: descriptionOpacity, y: descriptionY }}
            className="text-base md:text-lg lg:text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed"
          >
            Most companies do not lack effort or resources. They are constrained by hidden bottlenecks. Once these bottlenecks are removed, growth accelerates naturally.
          </motion.p>
        </div>
      </div>

      {/* MOBILE - Simple fade-in content, NO PARALLAX */}
      <div className="lg:hidden flex items-center justify-center min-h-[85vh] py-20">
        <div className="relative z-10 max-w-[1200px] mx-auto px-5 text-center">
          {/* Eyebrow */}
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ duration: 0.6 }}
            className="text-[13px] text-accent-blue font-semibold mb-5 uppercase tracking-widest"
          >
            About ForceM
          </motion.p>

          {/* Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-[36px] leading-[1.15] font-bold text-white mb-6"
          >
            We identify and remove
            <br />
            <span className="text-accent-blue"> growth bottlenecks</span>
          </motion.h1>

          {/* Description */}
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-[17px] leading-[1.7] text-gray-400 max-w-3xl mx-auto"
          >
            Most companies do not lack effort or resources. They are constrained by hidden bottlenecks. Once these bottlenecks are removed, growth accelerates naturally.
          </motion.p>
        </div>
      </div>
    </section>
  );
};

export default AboutHero;
